import { Alert, Col, Row, Table } from "react-bootstrap";
import { useCallback, useEffect, useState } from "react";

import {
  ErrorAlert,
  formatDate,
  Loading,
  Meter,
  PageHeader,
  UpgradeGrid,
} from "../../layout/secure/common";
import { currentUsage, messageFromError, usageHistory } from "../../utils/api";

function Usage() {
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    setError("");
    try {
      const [now, past] = await Promise.all([currentUsage(), usageHistory()]);
      setCurrent(now);
      setHistory(past.items || []);
    } catch (requestError) {
      setError(messageFromError(requestError, "Usage could not be loaded."));
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!current && !error) return <Loading label="Loading usage" />;

  const surveys = current?.surveys || {};
  const requests = current?.requests || {};
  const results = current?.results || {};
  const users = current?.users || {};
  const exhausted = surveys.remaining === 0 || requests.remaining === 0 || results.remaining === 0;

  return (
    <>
      <PageHeader title="Usage" description="Current License-defined period boundaries, plan capacity, and feature access." />
      <ErrorAlert error={error} onRetry={load} />
      {exhausted ? <Alert variant="warning">One or more plan allowances are full. Existing drafts and history remain available; the related new action is blocked server-side.</Alert> : null}
      <Row className="g-4">
        <Col lg="7">
          <section className="fera-panel">
            <div className="fera-panel-heading">
              <div><p className="fera-eyebrow">Current period</p><h2>Allowances</h2></div>
              <span>{formatDate(current?.usagePeriod?.startsAt)} &ndash; {formatDate(current?.usagePeriod?.endsAt)}</span>
            </div>
            <Meter label="Active surveys" value={surveys.active || 0} maximum={surveys.activeLimit} />
            <Meter label="Invitation requests" value={requests.used || 0} maximum={requests.included} />
            <Meter label="Submitted results" value={results.used || 0} maximum={results.included} />
            <Meter label="Product users" value={(users.active || 0) + (users.pendingInvitations || 0)} maximum={users.authorized} />
          </section>
        </Col>
        <Col lg="5">
          <section className="fera-panel h-100">
            <div className="fera-panel-heading"><div><p className="fera-eyebrow">Entitlements</p><h2>Current plan</h2></div></div>
            <dl className="fera-properties">
              <div><dt>Plan</dt><dd className="text-capitalize">{current?.planCode}</dd></div>
              <div><dt>Generic links</dt><dd>{current?.features?.genericLinks ? "Included" : "Unavailable"}</dd></div>
              <div><dt>HTML reports</dt><dd>{current?.features?.htmlReports ? "Included" : "Unavailable"}</dd></div>
              <div><dt>Data exports</dt><dd>{current?.features?.dataExports ? "Included" : "Paid plan required"}</dd></div>
              <div><dt>Rendered reports</dt><dd>{current?.features?.renderedReports ? "Included" : "Paid plan required"}</dd></div>
              <div><dt>Customer webhooks</dt><dd>{current?.features?.customerWebhooks ? "Included" : "Paid plan required"}</dd></div>
              <div><dt>Custom branding</dt><dd>{current?.features?.customBranding ? "Included" : "Complete or Beyond required"}</dd></div>
              <div><dt>Custom routing</dt><dd>{current?.features?.customRouting ? "Included" : "Beyond required"}</dd></div>
            </dl>
          </section>
        </Col>
      </Row>
      <UpgradeGrid access={current?.access} />
      <section className="fera-panel mt-4">
        <div className="fera-panel-heading"><div><p className="fera-eyebrow">History</p><h2>Usage periods</h2></div></div>
        {!history.length ? <p className="text-muted mb-0">No prior usage periods have been recorded.</p> : (
          <div className="table-responsive">
            <Table className="fera-table">
              <thead><tr><th>Period</th><th>Requests</th><th>Results</th><th>Surveys created</th></tr></thead>
              <tbody>
                {history.map(period => (
                  <tr key={period.usagePeriod?.id || period.usagePeriod?.startsAt}>
                    <td>{formatDate(period.usagePeriod?.startsAt)} &ndash; {formatDate(period.usagePeriod?.endsAt)}</td>
                    <td>{period.requests?.used || 0} / {period.requests?.included ?? "Unlimited"}</td>
                    <td>{period.results?.used || 0} / {period.results?.included ?? "Unlimited"}</td>
                    <td>{period.surveys?.createdThisPeriod || 0}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </section>
    </>
  );
}

export default Usage;
